import { type ScheduleScope, useClasses, useEvents, useLessons } from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { LessonManageModal, LessonPlanModal } from '../components/lessons';
import { Select } from '../components/ui';
import { PageHead, SUBJECT_GRAD } from '../design/forms';
import { useLook } from '../design/looks';
import { PlannerBlock, usePlanner } from '../schedule/Planner';
import { classTones, eventsPlan, lessonPlan, useFirstLessonDay } from '../schedule/lessons';
import { ShapesSchedule } from '../student/ShapesSchedule';
import { StudentSchedule } from '../student/StudentSchedule';
import type { StudentContext } from '../student/home/model';

/*
 * «Розклад» in «Форми 2» — a planner for every role (28.10, his refs «как людям привычнее —
 * планировщики, календари»): the week on a time axis, the lessons as blocks of their subject in their
 * time, the month and the day a tap away (`schedule/Planner.tsx`). The student and the family see the
 * child's week (`ShapesSchedule`); the teacher — their lessons, a block opens the lesson's plan (the
 * topic, what to bring, the test); the administration — a class's week, a block opens the lesson's
 * management (the format, a substitute, cancelling).
 */

export function SchedulePage() {
  const ctx = useCurrentContext();
  const look = useLook();
  if (ctx.kind === 'student')
    return look === 'shapes2' ? (
      <ShapesSchedule key={ctx.key} ctx={ctx} />
    ) : (
      <StudentSchedule key={ctx.key} ctx={ctx} />
    );
  if (ctx.kind === 'guardian') return <FamilySchedule key={ctx.key} />;
  return <StaffSchedule key={ctx.key} />;
}

/** The family sees the child's planner as the child does, without the way into a lesson. */
function FamilySchedule() {
  const current = useCurrentContext();
  const ctx = useMemo(
    () => ({ ...current, kind: 'student' }) as unknown as StudentContext,
    [current],
  );
  return <ShapesSchedule ctx={ctx} family />;
}

/** The teacher's lessons and a class's week for the administration. */
function StaffSchedule() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId);
  const [classId, setClassId] = useState<string>();
  const [managed, setManaged] = useState<string>();
  const adminClass = classId ?? classes.data?.[0]?.id;
  const scope: ScheduleScope = useMemo(
    () =>
      ctx.kind === 'teacher'
        ? { kind: 'teacher', memberId: ctx.memberId }
        : { kind: 'class', classId: adminClass ?? '' },
    [ctx, adminClass],
  );
  const perspective = scope.kind === 'teacher' ? 'teacher' : 'class';
  const first = useFirstLessonDay(ctx.schoolId, scope);
  const planner = usePlanner(first.date);
  const lessons = useLessons(ctx.schoolId, scope, planner.range.from, planner.range.to);
  const events = useEvents(ctx.schoolId, planner.range.from);
  const ready = first.ready && (scope.kind !== 'class' || adminClass !== undefined);
  const managedLesson = lessons.data?.find((l) => l.id === managed);
  const canManage = ctx.kind === 'admin' || ctx.kind === 'teacher';

  const happenings = eventsPlan(
    t,
    (events.data ?? []).filter(
      (e) => e.classId === null || (scope.kind === 'class' && e.classId === scope.classId),
    ),
    planner.range,
    () => void navigate({ to: '/events' }),
  );
  // the teacher's classes each in their own colour (the subject is mostly one)
  const classTone = classTones((lessons.data ?? []).map((l) => l.classLabel));
  const items = [
    ...happenings.items,
    ...(lessons.data ?? []).map((l) =>
      lessonPlan(
        t,
        l,
        perspective,
        canManage ? () => setManaged(l.id) : undefined,
        perspective === 'teacher' ? classTone : undefined,
      ),
    ),
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('schedule.title')}>
        {ctx.kind === 'admin' && (
          <Select
            aria-label={t('schedule.class')}
            className="!h-11 !rounded-full !pr-3 !pl-4"
            value={adminClass ?? ''}
            onChange={(e) => setClassId(e.target.value)}
          >
            {(classes.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
        )}
      </PageHead>
      <PlannerBlock
        planner={planner}
        items={ready ? items : []}
        notes={happenings.notes}
        pending={!ready || lessons.isPending}
        groupTone={(g) =>
          perspective === 'class' ? SUBJECT_GRAD[subjectLook(g).tone] : classTone(g)
        }
      />
      {managedLesson && ctx.kind === 'teacher' && (
        <LessonPlanModal lesson={managedLesson} onClose={() => setManaged(undefined)} />
      )}
      {managedLesson && ctx.kind === 'admin' && (
        <LessonManageModal
          lesson={managedLesson}
          schoolId={ctx.schoolId}
          onClose={() => setManaged(undefined)}
        />
      )}
    </div>
  );
}
